import { fmtCount } from './format';

/**
 * Confidence → rendering treatment (spec §0d + §5).
 *
 * `insufficient` is the only level that suppresses evidence, and it is gated in ONE
 * place — `PanelCard` — rather than per-evidence-component, so a new panel cannot
 * forget it and render an empty axis (F3). Nothing is ever hidden for low confidence:
 * on a new property nine of seventeen panels are sentence-only, and a page that hides
 * them greets every new client as broken.
 */
const TREATMENTS = {
  high: { showEvidence: true, opacityClass: '', captionKind: 'none' },
  medium: { showEvidence: true, opacityClass: '', captionKind: 'plain' },
  low: { showEvidence: true, opacityClass: 'opacity-60', captionKind: 'early' },
  insufficient: { showEvidence: false, opacityClass: '', captionKind: 'none' },
};

/**
 * Resolve a panel's confidence into `{ showEvidence, opacityClass, caption }`.
 *
 * `sampleSize` is absent on most panels — the caption is then omitted rather than
 * rendered as "Based on undefined" (F18).
 *
 * `sampleSizeLabel` is the payload's `sample_size_label` — the unit the count is in
 * ("searches", "complete months"). Without it the caption reads "Based on 5 — early
 * signal", where the number is meaningless; §G.2 shows it as "Based on 5 complete months
 * — early signal". Rendered verbatim; the front end never invents or pluralises it.
 *
 * @param {'high'|'medium'|'low'|'insufficient'|undefined} confidence
 * @param {number|null|undefined} sampleSize `panel.sample_size`
 * @param {string|null|undefined} sampleSizeLabel `panel.sample_size_label`
 */
export const confidenceTreatment = (confidence, sampleSize, sampleSizeLabel) => {
  const t = TREATMENTS[confidence] || TREATMENTS.high;
  const hasSample = Number.isFinite(Number(sampleSize)) && Number(sampleSize) > 0;
  const unit = typeof sampleSizeLabel === 'string' && sampleSizeLabel.trim()
    ? ` ${sampleSizeLabel.trim()}` : '';
  let caption = null;
  if (hasSample && t.captionKind === 'plain') caption = `Based on ${fmtCount(sampleSize)}${unit}`;
  if (hasSample && t.captionKind === 'early') caption = `Based on ${fmtCount(sampleSize)}${unit} — early signal`;
  return { showEvidence: t.showEvidence, opacityClass: t.opacityClass, caption };
};
